import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Clock, FileCode, CheckCircle2, XCircle, AlertTriangle, LockKeyhole } from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { useTimezone } from '../contexts/TimezoneContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  completed: { icon: CheckCircle2, color: 'text-[hsl(var(--success))]', bg: 'bg-[hsl(var(--success))]/10', label: 'Completed' },
  processing: { icon: Loader2, color: 'text-accent', bg: 'bg-accent/10', label: 'Processing', animate: true },
  pending: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Pending' },
  failed: { icon: XCircle, color: 'text-[hsl(var(--destructive))]', bg: 'bg-[hsl(var(--destructive))]/10', label: 'Failed' },
};

// Mar 2, 2026 — Discipline inference from the ISO 19650 file name.
// The 8th block onward (after the first 7 "-"-separated tokens) carries
// the role/discipline code that distinguishes drops within the same
// project — e.g. ``M-5901`` (MEP), ``A-0001`` (Architecture).
function _disciplineFromFileName(name) {
  if (!name) return null;
  const parts = (name || '').replace(/\.ifc$/i, '').split('-');
  const suffix = parts.length >= 8 ? parts.slice(7).join('-') : '';
  if (/^M-/i.test(suffix)) return { label: 'MEP',          color: '#BA7517' };
  if (/^A-/i.test(suffix)) return { label: 'Architecture', color: '#3B6D11' };
  if (/^S-/i.test(suffix)) return { label: 'Structure',    color: '#185FA5' };
  if (/^E-/i.test(suffix)) return { label: 'Electrical',   color: '#7B1FA2' };
  return null;
}

export default function HistoryPage() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { formatDateTime, formatDuration } = useTimezone();

  useEffect(() => {
    fetchAudits();
    const interval = setInterval(fetchAudits, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAudits = async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/audits`);
      if (resp.ok) {
        const json = await resp.json();
        setAudits(json.audits || []);
      }
    } catch (err) {
      console.error('Failed to fetch audits:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="history-title">
            AIM Register History
          </h1>
          <p className="text-muted-foreground">
            View all past audit runs and their results
          </p>
        </div>

        {audits.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileCode className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No audits yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Run your first audit to see results here</p>
              <Button onClick={() => navigate('/dashboard')} data-testid="start-audit-btn">
                Start New Audit
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {audits.map((audit) => {
              const config = STATUS_CONFIG[audit.status] || STATUS_CONFIG.pending;
              const StatusIcon = config.icon;
              const summary = audit.summary || {};

              return (
                <Card
                  key={audit.id}
                  className="hover:border-accent/50 transition-colors cursor-pointer"
                  onClick={() => audit.status === 'completed' ? navigate(`/results/${audit.id}`) : null}
                  data-testid={`audit-card-${audit.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2.5 rounded-lg ${config.bg}`}>
                        <StatusIcon className={`h-5 w-5 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground truncate">
                            {audit.ifc_file_name || 'Unnamed Audit'}
                          </h3>
                          {(audit.project_code || audit.project_name) && (
                            <Badge variant="secondary" className="text-xs shrink-0" data-testid={`history-audit-project-${audit.id}`}>
                              {audit.project_code || audit.project_name}
                            </Badge>
                          )}
                          {audit.is_read_only && (
                            <Badge variant="outline" className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" data-testid={`history-demo-access-${audit.id}`}>
                              <LockKeyhole className="h-3 w-3" /> Demo · Read-only
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs shrink-0">
                            {config.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span>{formatDateTime(audit.created_at)}</span>
                          {(() => {
                            const dur = formatDuration(audit.created_at, audit.completed_at);
                            return dur ? <span data-testid={`audit-duration-${audit.id}`}>· {dur}</span> : null;
                          })()}
                          {audit.total_elements > 0 && (
                            <span data-testid={`audit-coverage-${audit.id}`}>
                              Validated{' '}
                              {(audit.parsed_elements ?? audit.total_elements).toLocaleString()}
                              {' / '}
                              {audit.total_elements.toLocaleString()} elements
                              {audit.validation_coverage_percent != null && (
                                <>
                                  {' '}({Number(audit.validation_coverage_percent).toFixed(0)}%)
                                </>
                              )}
                            </span>
                          )}
                          {audit.validation_mode && audit.validation_mode !== 'full' && (
                            <Badge variant="outline" className="text-[10px] border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]">
                              {String(audit.validation_mode).toUpperCase()}
                            </Badge>
                          )}
                          {audit.bep_file_name && <span>+ BEP</span>}
                          {audit.processing_step && audit.status === 'processing' && (
                            <span className="text-accent">{audit.processing_step}</span>
                          )}
                          {/* Mar 2, 2026 — Discipline badge inferred from
                              the ISO 19650 file-name suffix. ``M-…`` →
                              MEP, ``A-…`` → Architecture, etc. Only
                              renders when the suffix matches a known
                              role letter; falls silent otherwise. */}
                          {(() => {
                            const disc = _disciplineFromFileName(audit.ifc_file_name);
                            return disc ? (
                              <span
                                data-testid={`audit-discipline-${audit.id}`}
                                style={{
                                  color: disc.color,
                                  border: `1px solid ${disc.color}`,
                                  borderRadius: '4px',
                                  padding: '1px 6px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  letterSpacing: '0.05em',
                                }}
                              >
                                {disc.label}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        {/* Mar 2, 2026 — Rule coverage line. ``active_rule_count``
                            is the count of confirmed rules; ``applicable_checks_count``
                            is how many ⟨rule × element⟩ checks survived the
                            applicability engine. Renders only when at least one
                            active rule exists so audits without custom BEP
                            rules stay visually clean. */}
                        {audit.active_rule_count > 0 && (
                          <div
                            className="mt-1 text-xs text-muted-foreground"
                            data-testid={`audit-rule-coverage-${audit.id}`}
                          >
                            {audit.active_rule_count} rules · {(audit.applicable_checks_count || 0).toLocaleString()} checks applied
                          </div>
                        )}
                      </div>

                      {audit.status === 'completed' && summary.pass_rate != null && (
                        <div className="text-right shrink-0">
                          <div className="text-lg font-bold text-foreground">{summary.pass_rate}%</div>
                          <div className="text-xs text-muted-foreground">pass rate</div>
                          {audit.schema_version && (
                            <div
                              className="text-[10px] text-muted-foreground mt-0.5"
                              data-testid={`audit-schema-${audit.id}`}
                            >
                              {audit.schema_version}
                            </div>
                          )}
                          {/* Mar 2, 2026 — Tiny authoring-tool chip
                              so authoring-tool/schema mismatches
                              across a project are caught here (all
                              audits ArchiCAD/IFC2X3 but rules target
                              IFC4 → visible at a glance). Reads from
                              the parser-persisted snapshot. */}
                          {summary?.authoring_tool?.display && summary.authoring_tool.source !== 'unknown' && (
                            <div
                              className="text-[10px] text-muted-foreground mt-0.5 italic truncate max-w-[180px]"
                              title={summary.authoring_tool.originating_system || summary.authoring_tool.display}
                              data-testid={`audit-authoring-${audit.id}`}
                            >
                              {summary.authoring_tool.display}
                            </div>
                          )}
                        </div>
                      )}

                      {audit.status === 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/results/${audit.id}`); }}
                          data-testid={`view-results-${audit.id}`}
                        >
                          View Results
                        </Button>
                      )}
                    </div>

                    {audit.status === 'failed' && audit.error_message && (
                      <div className="mt-3 p-2 bg-[hsl(var(--destructive))]/5 rounded text-xs text-[hsl(var(--destructive))]">
                        {audit.error_message}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
